import { useTranslation } from "react-i18next";
import { Address } from "../../../../redux/apiSpecifications/apiSubjects";
import { FormRow } from "../../../../style/form";
import { InputTextStyled } from "../../../../style/Input";
import { RowSpacer } from "../../../../style/containers";
import { useEffect, useMemo, useState } from "react";
import FormModal from "../../../Modals/FormModal";
import useFormData from "../../../../hooks/useFormData";
import { Urls } from "../../../../config/urlBuilder";
import { getAzureMapAddressConfig } from "../../../../config/azureConfig";
import {
  AutocompleteWrapper,
  ResultItem,
  Results,
  SearchBox,
} from "../../../AzureMapsAddress/AddressAutocomplete";
import { Input } from "antd";
import { IconCancel } from "../../../../config/icons";
import { toFullAddress } from "../../../../utils/address";
import SelectProvince from "../../../Inputs/SelectProvince";
import SelectCountry from "../../../Inputs/SelectCountry";
import { checkPostalCode, isNotEmpty } from "../../../../validation/common";

interface ISubjectAddressFormProps {
  address: Address | undefined;
  options?: { civicMandatory: boolean };
  onChange?: (value: any, field: string) => void;
}

const SubjectAddressForm = ({
  address,
  options,
  onChange,
}: ISubjectAddressFormProps) => {
  const { t } = useTranslation();

  const handleOnChange = (value: any, field: string) => {
    if (onChange) onChange(value, field);
  };

  return (
    <>
      <FormRow>
        <InputTextStyled
          label={t("address-street-label")}
          tooltip={t("address-street-tooltip")}
          placeholder={t("address-street-placeholder")}
          onChange={(street: string) => {
            handleOnChange(street, "street");
          }}
          value={address?.street}
          required={true}
        />
      </FormRow>

      <FormRow>
        <InputTextStyled
          label={t("address-civic-label")}
          tooltip={t("address-civic-tooltip")}
          placeholder={t("address-civic-placeholder")}
          onChange={(civic: string) => handleOnChange(civic, "civic")}
          value={address?.civic}
          required={options?.civicMandatory}
        />

        <RowSpacer />

        <InputTextStyled
          label={t("address-postalcode-label")}
          tooltip={t("address-postalcode-tooltip")}
          placeholder={t("address-postalcode-placeholder")}
          onChange={(postalCode: string) =>
            handleOnChange(postalCode, "postalCode")
          }
          value={address?.postalCode}
          required={true}
        />
      </FormRow>

      <FormRow>
        <InputTextStyled
          label={t("address-city-label")}
          tooltip={t("address-city-tooltip")}
          placeholder={t("address-city-placeholder")}
          onChange={(city: string) => handleOnChange(city, "city")}
          value={address?.city}
          required={true}
        />
      </FormRow>

      <FormRow>
        <SelectProvince
          onChange={(province: string) => handleOnChange(province, "province")}
          value={address?.province}
        />

        <RowSpacer />

        <SelectCountry
          onChange={(country: string) => handleOnChange(country, "country")}
          value={address?.country}
        />
      </FormRow>
    </>
  );
};

export default SubjectAddressForm;

type AddressAutocompleteResult = {
  street?: string;
  civic?: string;
  postalCode?: string;
  city?: string;
  province?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
};

interface IAddressAutocompleteProps {
  onSelectAddress: (address: AddressAutocompleteResult) => void;
}

const AddressAutocomplete = ({
  onSelectAddress,
}: IAddressAutocompleteProps) => {
  const { t } = useTranslation();

  const [query, setQuery] = useState<string | undefined>();
  const [isSearching, setIsSearching] = useState(false);
  const [timeoutSearch, setTimeoutSearch] =
    useState<ReturnType<typeof setTimeout>>();
  const [execSearch, setExecSearch] = useState(0);
  const [results, setResults] = useState<AddressAutocompleteResult[]>([]);

  useEffect(() => {
    handleSearch();
  }, [execSearch]);

  const handleQueryChange = (q: string) => {
    setQuery(q);

    if (q.length > 5) {
      if (timeoutSearch) clearTimeout(timeoutSearch);

      const newInterval = setTimeout(() => setExecSearch(Date.now()), 500);

      setTimeoutSearch(newInterval);
    }
  };

  const handleKeyup = (e: React.KeyboardEvent) => {
    if (e.code === "Enter") handleSearch();
  };

  const handleSearch = async () => {
    if (!query) return;

    setIsSearching(true);

    const limit = 6;

    const url = Urls.azureAddress(query, "it", limit);

    try {
      const responseIT = await fetch(url + "&countrySet=IT");
      const dataIT = await responseIT.json();
      const addressesIT: any[] = dataIT.results;

      const azureMapAddressConfig = getAzureMapAddressConfig();

      if (addressesIT.length < limit) {
        const response = await fetch(url);
        const data = await response.json();
        const addresses = data.results;

        addresses.forEach((address: any) => {
          const resultFind = addressesIT.find((_) => _.id === address.id);

          if (!resultFind) {
            addressesIT.push(address);
          }
        });
      }

      if (addressesIT.length > 0) {
        const newResults = addressesIT.map(
          (r: any) =>
            ({
              street: r.address.streetName,
              civic: r.address.streetNumber,
              postalCode: r.address.postalCode,
              city: r.address.municipality,
              province: r.address.countrySecondarySubdivision,
              country: r.address.country,
              latitude: r.position.lat,
              longitude: r.position.lon,
            } as AddressAutocompleteResult)
        );

        setResults(newResults);
      }
    } catch (e) {
      console.log(e);
    }

    setIsSearching(false);
  };

  const handleSelectAddress = (address: AddressAutocompleteResult) => {
    setResults([]);

    onSelectAddress(address);
  };

  const handleClearQuery = () => {
    handleQueryChange("");

    setResults([]);
  };

  return (
    <AutocompleteWrapper>
      <SearchBox>
        <Input
          placeholder={t("address-automplete-input-placeholder")!}
          onChange={(e) => handleQueryChange(e.currentTarget.value)}
          readOnly={isSearching}
          value={query}
          onKeyUp={handleKeyup}
          addonAfter={
            <div className="clear-query" onClick={handleClearQuery}>
              <IconCancel />
            </div>
          }
        />
      </SearchBox>

      {!isSearching && results && results.length > 0 && (
        <Results>
          {results.map((r, i) => (
            <ResultItem key={i} onClick={() => handleSelectAddress(r)}>
              {toFullAddress(r)}
            </ResultItem>
          ))}
        </Results>
      )}
    </AutocompleteWrapper>
  );
};

interface ISubjectAddressModalProps {
  open: boolean;
  address: Address | undefined;
  options?: { civicMandatory: boolean };
  onCancel: () => void;
  onConfirm: (address: Address) => void;
}

export const SubjectAddressModal = ({
  open,
  address,
  options,
  onCancel,
  onConfirm,
}: ISubjectAddressModalProps) => {
  const {
    formData: _address,
    setFormData: setAddress,
    onChangeFormData: onChangeAddress,
  } = useFormData<Address>();

  useEffect(() => {
    if (open) {
      setAddress(address);
    }
  }, [open]);

  const handleOnConfirmAddress = () => {
    onConfirm({ ..._address! });
  };

  const isSubjectAddressValid = useMemo(() => {
    if (_address === undefined) return false;

    const { street, civic, city, postalCode, province, country } = _address;

    return (
      isNotEmpty(street) &&
      (options?.civicMandatory === true ? isNotEmpty(civic) : true) &&
      isNotEmpty(city) &&
      checkPostalCode(postalCode?.toString() || "") &&
      isNotEmpty(province) &&
      isNotEmpty(country)
    );
  }, [_address, options]);

  return (
    <FormModal
      open={open}
      width={700}
      isConfirmButtonDisabled={!isSubjectAddressValid}
      onCancel={onCancel}
      onConfirm={handleOnConfirmAddress}
    >
      {_address && (
        <>
          <AddressAutocomplete
            onSelectAddress={(address) =>
              setAddress({ ..._address, ...address })
            }
          />

          <SubjectAddressForm address={_address} onChange={onChangeAddress} />
        </>
      )}
    </FormModal>
  );
};
